import { useState } from 'react';

import CashRegistryResetService from '../../services/cash/CashRegistryResetService';
import useAlerts from '../useAlerts';
import useConfirmModal from '../useConfirmModal';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';

export interface CashRegistryResetSummary {
  sessionsCount: number;
  closuresCount: number;
  movementsCount: number;
  salesLinkedCount: number;
}

export interface CashRegistryResetResult {
  sessionsDeleted: number;
  closuresDeleted: number;
  movementsDeleted: number;
  salesUnlinked: number;
}

/**
 * 🧹 Panel solo-SUPPORT: limpieza en bloque de los REGISTROS de caja
 * (aperturas/sesiones, cortes X/Z, movimientos) en la base de datos.
 *
 * ⚠️ No confundir con `useCashReset` (limpia el estado local de UI del
 * flujo de cierre de caja) — este hook llama al backend y elimina datos
 * reales.
 */
export const useCashRegistryReset = () => {
  const { show: showAlert } = useAlerts();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const [summary, setSummary] = useState<CashRegistryResetSummary | null>(null);
  const [lastResult, setLastResult] = useState<CashRegistryResetResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handlerLoadSummary = async () => {
    try {
      setIsLoading(true);

      const response = await CashRegistryResetService.getSummary();

      if (response?.statusCode === 200 && response.data) {
        setSummary(response.data);
      }
    } catch (error: any) {
      handle(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlerResetAll = () => {
    confirm({
      title: '¿Eliminar TODOS los registros de caja?',
      message:
        'Se eliminarán todas las aperturas/sesiones, cortes X/Z y movimientos de caja del sistema. Las ventas NO se ven afectadas (solo se suelta su referencia a la sesión eliminada). Esta acción no se puede deshacer.',
      icon: 'warning',
      confirmText: 'Eliminar todo',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          showLoadingIndicator();

          const response = await CashRegistryResetService.execute();

          if (response?.statusCode === 200) {
            showAlert(response.type, response.title, response.message);
            setLastResult(response.data);
            setSummary({
              sessionsCount: 0,
              closuresCount: 0,
              movementsCount: 0,
              salesLinkedCount: 0,
            });
          } else {
            showAlert('error', 'Error', 'No se pudo limpiar la caja.');
          }
        } catch (error: any) {
          handle(error);
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };

  return {
    summary,
    lastResult,
    isLoading,
    handlerLoadSummary,
    handlerResetAll,
  };
};

export default useCashRegistryReset;
